<template>
  <b-card no-body aria-controls="overlay-background">
    <b-overlay :show="loading" :variant="isDarkMode ? 'dark' : 'light'">
      <template v-slot:overlay>
        <loading-logo />
      </template>
      <b-card-body class="p-0">
        <vue-apex-charts
          type="bar"
          height="400"
          :options="chartOptions"
          :series="processedSeries"
        />
      </b-card-body>
    </b-overlay>
  </b-card>
</template>

<script>
import { BCard, BCardBody, BOverlay } from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import store from '@/store'
import moment from 'moment-timezone'
import LoadingLogo from '../miscellaneous/LoadingLogo.vue'

export default {
  components: {
    VueApexCharts,
    BCard,
    BCardBody,
    BOverlay,
    LoadingLogo,
  },
  props: {
    iotData: {
      type: Array,
      default: () => [],
    },
    list: {
      type: Object,
      default: () => ({ list: [] }),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isDarkMode() {
      return store.state.appConfig.layout.skin === 'dark'
    },
    processedSeries() {
      const seriesMap = new Map()
      this.iotData.forEach(originalSeries => {
        originalSeries.data.forEach(dataPoint => {
          const val = dataPoint.val
          const listDetails = this.findListDetailsByValue(val)
          const seriesName = listDetails.text
          const color = dataPoint.fillColor

          if (!seriesMap.has(val)) {
            seriesMap.set(val, {
              name: seriesName,
              data: [],
              color: color,
            })
          }

          seriesMap.get(val).data.push({
            x: dataPoint.x,
            y: dataPoint.y,
            fillColor: color,
          })
        })
      })
      return Array.from(seriesMap.values())
    },
    chartOptions() {
      return {
        chart: {
          locales: [
            {
              name: 'es',
              options: {
                months: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
                shortMonths: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
                days: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
                shortDays: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
                toolbar: {
                  exportToSVG: 'Descargar SVG',
                  exportToPNG: 'Descargar PNG',
                  exportToCSV: 'Descargar CSV',
                  menu: 'Menu',
                  selection: 'Selección',
                  selectionZoom: 'Selección Zoom',
                  zoomIn: 'Acercar',
                  zoomOut: 'Alejar',
                  pan: 'Mover',
                  reset: 'Reiniciar Zoom',
                },
              },
            },
          ],
          defaultLocale: 'es',
          toolbar: { show: true },
        },
        fill: {
          type: 'solid',
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded',
            distributed: true,
          },
        },
        dataLabels: { enabled: false },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent'],
        },
        legend: {
          show: this.processedSeries.length > 1,
          labels: {
            colors: this.isDarkMode ? '#fff' : '#000',
          },
        },
        tooltip: {
          enabled: true,
          y: {
            formatter: (value, { seriesIndex, dataPointIndex, w }) => {
              return w.config.series[seriesIndex].name
            },
          },
        },
        yaxis: {
          show: false,
          labels: {
            style: { colors: '#000' },
            offsetX: -6,
            formatter: value => {
              const listItem = this.list.list.find(item => item.key === value);
              return listItem ? listItem.text : value;
            },
          },
          min: 0,
          max: 1,
          tickAmount: 1,
        },
        xaxis: {
          type: 'datetime',
          labels: {
            style: { colors: '#000' },
            datetimeFormatter: {
              year: 'yyyy',
              month: "MMM 'yy",
              day: 'dd MMM',
              hour: 'HH:mm',
            },
            formatter: value => moment.utc(value).local().format('DD/MM hh:mm'),
            offsetX: 20,
          },
        },
        noData: {
          text: 'No hay datos para mostrar',
          align: 'center',
          verticalAlign: 'middle',
          offsetX: 0,
          offsetY: 0,
          style: {
            color: '#008FFB',
            fontSize: '26px',
          },
        },
      };
    },
  },
  methods: {
    findListDetailsByValue(value) {
      if (!this.list || !this.list.list) {
        return { text: value, color: '#000000' };
      }
      return this.list.list.find(item => item.key === value) || { text: value, color: '#000000' };
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/chart-apex.scss";

.apexcharts-toolbar {
  z-index: 0 !important;
}
</style>
